import { Navigate, useLocation } from 'react-router-dom';
import { ReactElement, useEffect, useState } from 'react';
import { tokenStorage } from '../lib/secureStorage';
import { isAccessTokenExpired, refreshAccessToken } from '../lib/authRefresh';
import { Box, CircularProgress } from '@mui/material';

interface ProtectedRouteProps {
  children: ReactElement;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const location = useLocation();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshAttempted, setRefreshAttempted] = useState(false);

  const accessToken = tokenStorage.getAccessToken();
  const refreshToken = tokenStorage.getRefreshToken();
  const needsRefresh = !!refreshToken && (!accessToken || isAccessTokenExpired(accessToken));

  useEffect(() => {
    const attemptRefresh = async () => {
      if (needsRefresh && !refreshAttempted && !isRefreshing) {
        setIsRefreshing(true);
        setRefreshAttempted(true);

        try {
          await refreshAccessToken(refreshToken ?? undefined);
        } catch {
          tokenStorage.clearTokens();
        } finally {
          setIsRefreshing(false);
        }
      }
    };

    void attemptRefresh();
  }, [needsRefresh, refreshToken, refreshAttempted, isRefreshing]);

  if (isRefreshing || (needsRefresh && !refreshAttempted)) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const access = tokenStorage.getAccessToken();
  const hasValidAccess = !!access && !isAccessTokenExpired(access);
  // Allow one render cycle while refresh is in flight if a refresh token still exists.
  const isAuthenticated = hasValidAccess || (!refreshAttempted && !!tokenStorage.getRefreshToken());

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

export default ProtectedRoute;
